import { MissionSummaryIndustryIcon } from "./MissionSummaryIndustryIcon";
import {
  resolveMissionSummaryIndustries,
  type MissionHeatmapIndustryInput,
  type MissionHeatmapProjectionInput,
} from "./missionHeatmapDisplay";
import { resolveMissionSummaryIndustryPresentation } from "./missionSummaryIndustryPresentation";

export type ProfileMissionSummaryCardLabels = Readonly<{
  title: string;
  emptyLabel: string;
  industryMissionCount: (count: number) => string;
  viewAllMissions?: string;
  editAriaLabel?: string;
}>;

export type ProfileMissionSummaryCardProps = Readonly<{
  className?: string;
  componentInstance?: string;
  industries?: readonly MissionHeatmapIndustryInput[];
  labels: ProfileMissionSummaryCardLabels;
  onEdit?: () => void;
  onViewAll?: () => void;
  projection?: MissionHeatmapProjectionInput;
}>;

function MissionSummaryEditIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MissionSummaryChevronIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m9 6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ProfileMissionSummaryCard({
  className,
  componentInstance = "profileMissionSummaryCard",
  industries,
  labels,
  onEdit,
  onViewAll,
  projection,
}: ProfileMissionSummaryCardProps) {
  const industryEntries = resolveMissionSummaryIndustries(projection, industries);
  const showViewAll = Boolean(labels.viewAllMissions && onViewAll);

  return (
    <section
      className={["profile-mission-summary-card", className].filter(Boolean).join(" ")}
      data-component-instance={componentInstance}
      data-mission-summary-industry-count={String(industryEntries.length)}
    >
      <div className="profile-mission-summary-card__header profile-section-header">
        <div className="profile-section-header__title-group">
          <h2>{labels.title}</h2>
          {onEdit && labels.editAriaLabel ? (
            <button
              type="button"
              className="profile-mission-summary-card__edit-button"
              aria-label={labels.editAriaLabel}
              data-component-instance={`${componentInstance}EditButton`}
              onClick={onEdit}
            >
              <MissionSummaryEditIcon className="profile-mission-summary-card__header-icon" />
            </button>
          ) : null}
        </div>
        {showViewAll ? (
          <div className="profile-section-header__trailing">
            <button
              type="button"
              className="profile-mission-summary-card__view-all"
              data-component-instance={`${componentInstance}ViewAllButton`}
              onClick={onViewAll}
            >
              {labels.viewAllMissions}
              <MissionSummaryChevronIcon className="profile-mission-summary-card__header-icon" />
            </button>
          </div>
        ) : null}
      </div>

      {industryEntries.length > 0 ? (
        <ul className="profile-mission-summary-card__industries">
          {industryEntries.map((entry) => {
            const presentation = resolveMissionSummaryIndustryPresentation(entry.industry);
            return (
              <li key={entry.industry}>
                <span className="profile-mission-summary-card__industry-label">
                  <MissionSummaryIndustryIcon
                    className="profile-mission-summary-card__industry-icon"
                    presentation={presentation}
                  />
                  <span>{entry.industry}</span>
                </span>
                <span className="profile-mission-summary-card__count">
                  {labels.industryMissionCount(entry.missionCount)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="profile-mission-summary-card__empty">{labels.emptyLabel}</p>
      )}
    </section>
  );
}
